import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { getOccurrencesForJob } from "../utils/getOccurrencesForJob";
import { isRecurring, makeOccurrenceDateMatcher } from "../utils/recurringOccurrenceUtils";
import {
  fetchDailyLaborDays,
  summarizeDailyLabor,
  usersToMap,
  UNASSIGNED_JOB,
} from "../utils/techDailyPay";

/**
 * FinancialsDashboard.js (Priority Pest Defense)
 *
 * Routed at /Financials and also embedded as the "Overview" tab of the
 * Accounting page, so it has no full-page / fixed layout.
 *
 * Where each number comes from:
 *   Cash In / Cash Out   transactions ledger (Deposits, Expenses, Bank
 *                        Statement and Job Estimate all mirror into it) +
 *                        older subcontractorPay docs that were never mirrored.
 *                        Bank lines marked "do not count" are left out.
 *   Tech Daily Pay       techDailyPay.summarizeDailyLabor over the dailyLabor
 *                        days in range (never raw workers[].dailyPay /
 *                        totalCost - older docs used the wrong rate).
 *   Job rows             jobs + job_totals + jobs/{id}/financials/v1.
 *                        Labor per job = actual Tech Daily Pay for that job in
 *                        the range when recorded, otherwise the estimate on
 *                        Job Financials. Never both.
 *                        Recurring jobs: price = invoices for visits in range
 *                        (or completed visits x per-visit fee when no invoice).
 */

function parseDate(d) {
  if (!d) return null;
  if (typeof d === "object" && typeof d.toDate === "function") return moment(d.toDate());
  if (typeof d === "object" && d.seconds) return moment.unix(d.seconds);
  if (d instanceof Date) return moment(d);
  const formats = [moment.ISO_8601, "YYYY-MM-DD", "YYYY-MM", "YYYY", "MM-DD-YYYY", "MM/DD/YYYY"];
  for (const f of formats) {
    const m = moment(d, f, true);
    if (m.isValid()) return m;
  }
  return null;
}

function rangeFor(mode, anchor) {
  const m = parseDate(anchor) || moment();
  if (mode === "day") return { start: m.clone().startOf("day"), end: m.clone().endOf("day") };
  if (mode === "week") return { start: m.clone().startOf("isoWeek"), end: m.clone().endOf("isoWeek") };
  if (mode === "month") return { start: m.clone().startOf("month"), end: m.clone().endOf("month") };
  return { start: m.clone().startOf("year"), end: m.clone().endOf("year") };
}

const readStorage = (key, fallback) => {
  try {
    return localStorage.getItem(key) || fallback;
  } catch (e) {
    return fallback;
  }
};
const writeStorage = (key, value) => {
  try {
    localStorage.setItem(key, value);
  } catch (e) {
    /* storage blocked - ignore */
  }
};

const DASHBOARD_CATEGORIES = [
  "Materials",
  "Chemicals / Pest Supplies",
  "Meals / Food",
  "Fuel / Gas",
  "Labor",
  "Subcontractor",
  "Permit",
  "Overhead",
  "Bank Fee",
  "Credit Card Payment",
  "Internal Transfer",
  "Owner Transfer / Capital",
  "Personal / Owner Draw",
  "Personal Life Insurance",
  "Refund / Reimbursement",
  "Bank Correction / Reversal",
  "Utilities",
  "Insurance",
  "Vehicle Maintenance",
  "Software / Subscriptions",
  "Misc",
];

const ACCOUNTING_ACTION_LABELS = {
  count_as_new: "Count in books",
  count_as_income: "Count as income",
  count_as_expense: "Count as business expense",
  matched_existing_do_not_count: "Matched existing - do not count again",
  credit_card_payment_transfer: "Credit card payment / transfer - do not count",
  internal_transfer_do_not_count: "Internal transfer - do not count",
  owner_contribution_do_not_count: "Owner contribution / capital - do not count as income",
  owner_draw_personal_do_not_count: "Personal / owner draw - do not count as business expense",
  refund_reimbursement_offset: "Refund / reimbursement - offset/review",
  bank_correction_do_not_count: "Bank correction / reversal - do not count",
};

const DO_NOT_COUNT_ACTIONS = new Set([
  "matched_existing_do_not_count",
  "credit_card_payment_transfer",
  "internal_transfer_do_not_count",
  "owner_contribution_do_not_count",
  "owner_draw_personal_do_not_count",
  "bank_correction_do_not_count",
]);

const formatMoney = (value) =>
  Number(value || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

const num = (v) => {
  if (v === null || v === undefined || v === "") return 0;
  if (typeof v === "string") {
    const n = Number(v.replace(/[^0-9.-]/g, ""));
    return Number.isFinite(n) ? n : 0;
  }
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const strictMoney = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  const cleaned = String(value).trim().replace(/[$,\s]/g, "");
  return /^-?\d+(\.\d{1,2})?$/.test(cleaned) ? Number(cleaned) : 0;
};
const firstMoney = (...values) => {
  for (const v of values) {
    const n = strictMoney(v);
    if (n) return n;
  }
  return 0;
};

const getDefaultAccountingAction = (entry = {}) => {
  if (entry.accountingAction) return entry.accountingAction;
  if (entry.matchedExistingPayment) return "matched_existing_do_not_count";
  if (entry.accountType === "creditCard" && entry.type === "moneyIn") return "credit_card_payment_transfer";
  if (entry.category === "Credit Card Payment") return "credit_card_payment_transfer";
  if (entry.category === "Internal Transfer") return "internal_transfer_do_not_count";
  if (entry.category === "Owner Transfer / Capital") return "owner_contribution_do_not_count";
  if (entry.category === "Personal / Owner Draw" || entry.category === "Personal Life Insurance") return "owner_draw_personal_do_not_count";
  if (entry.category === "Refund / Reimbursement") return "refund_reimbursement_offset";
  if (entry.category === "Bank Correction / Reversal") return "bank_correction_do_not_count";
  if (entry.type === "moneyIn") return "count_as_income";
  return "count_as_expense";
};

const bankEntryCountsInBooks = (entry = {}) => {
  if (entry.matchedExistingPayment) return false;
  return ["count_as_new", "count_as_income", "count_as_expense"].includes(getDefaultAccountingAction(entry));
};

const sourceLabel = (item = {}) => {
  if (item.sourceCollection === "bankStatementEntries" || item.source === "bankStatementEntries") return "Bank Statement";
  if (item.sourceCollection === "deposits") return "Deposits / Check Registry";
  if (item.sourceCollection === "monthlyBills") return "Monthly Bills";
  if (item.sourceCollection === "bankDeductions") return "Bank Deductions";
  if (item.sourceCollection === "subcontractorPay") return "Subcontractor Pay";
  if (item.sourceCollection === "employeePay") return "Employee Pay";
  return item.method || item.source || "Manual";
};

const baseJobId = (id) => String(id || "").split("__")[0];

const invoiceTotal = (inv = {}) => {
  const direct = firstMoney(inv.amount, inv.price, inv.rate, inv.total);
  if (direct) return direct;
  return (Array.isArray(inv.serviceRows) ? inv.serviceRows : []).reduce(
    (s, r) => s + (r?.total != null && r?.total !== "" ? num(r.total) : num(r?.quantity || 1) * num(r?.amount)),
    0
  );
};

const recurringVisitFee = (job = {}, key = "") => {
  const initial = strictMoney(job.initialAmount);
  if (initial > 0 && key && key === String(job.startDate || "").slice(0, 10)) return initial;
  return firstMoney(job.recurringInvoiceAmount, job.monthlyAmount, job.monthlyFee, job.estimatedTotal);
};

// Overhead the same way Job Financials stores it (override / flat / percent).
const finOverhead = (fin = {}, price = 0) => {
  if (fin.overheadOverride !== null && fin.overheadOverride !== undefined && fin.overheadOverride !== "") {
    return Math.max(num(fin.overheadOverride), 0);
  }
  if (fin.overheadAllocMethod === "flat") return Math.max(num(fin.overheadAllocValue), 0);
  if (fin.overheadAllocMethod === "percent") return (Math.max(num(fin.overheadAllocValue), 0) / 100) * price;
  return num(fin.__allocatedOverhead);
};

const S = {
  note: {
    border: "1px solid #93c5fd",
    background: "#eff6ff",
    color: "#1e3a8a",
    borderRadius: 10,
    padding: 12,
    margin: "12px 0",
    lineHeight: 1.45,
    fontSize: 14,
  },
  grid: (min = 180) => ({ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`, gap: 12, marginTop: 16 }),
  card: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 12 },
  cardLabel: { fontSize: 13, color: "#475569", fontWeight: 700 },
  cardValue: { fontSize: 18, fontWeight: 800, marginTop: 4 },
  cardSub: { fontSize: 12, color: "#64748b", marginTop: 2 },
  tag: (bg, color) => ({ display: "inline-block", fontSize: 11, padding: "1px 6px", borderRadius: 999, background: bg, color, marginLeft: 4 }),
};

const Card = ({ label, value, sub, color }) => (
  <div style={S.card}>
    <div style={S.cardLabel}>{label}</div>
    <div style={{ ...S.cardValue, color: color || "#0f172a" }}>{value}</div>
    {sub && <div style={S.cardSub}>{sub}</div>}
  </div>
);

export default function FinancialsDashboard({ onOpenTab } = {}) {
  const [mode, setMode] = useState(() => readStorage("financialMode", "day"));
  const [anchor, setAnchor] = useState(() => readStorage("financialAnchor", moment().format("YYYY-MM-DD")));
  const [tab, setTab] = useState("jobs"); // "jobs" | "transactions" | "bankStatements"

  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [laborDays, setLaborDays] = useState([]);
  const [spendingDays, setSpendingDays] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [bankStatementEntries, setBankStatementEntries] = useState([]);
  const [jobTotals, setJobTotals] = useState({ byId: {}, byWo: {} });
  const [priceDrafts, setPriceDrafts] = useState({});
  const [txForm, setTxForm] = useState({
    date: moment().format("YYYY-MM-DD"),
    type: "expense",
    amount: "",
    category: "Materials",
    vendor: "",
    memo: "",
    jobId: "",
    method: "manual",
  });
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // Load data
  useEffect(() => {
    let cancelled = false;
    const safe = async (name) => {
      try {
        const snap = await getDocs(collection(db, name));
        return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      } catch (err) {
        console.warn(`Could not load ${name}`, err);
        return [];
      }
    };

    (async () => {
      setLoading(true);
      try {
        const [
          allJobs,
          totals,
          usersData,
          spend,
          txRows,
          subPayRows,
          bankRows,
          invoiceRows,
          days,
        ] = await Promise.all([
          safe("jobs"),
          safe("job_totals"),
          safe("users"),
          safe("dailySpending"),
          safe("transactions"),
          safe("subcontractorPay"),
          safe("bankStatementEntries"),
          safe("approvedInvoices"),
          fetchDailyLaborDays().catch(() => []),
        ]);

        const withFin = await Promise.all(
          allJobs.map(async (j) => {
            let fin = {};
            try {
              const finSnap = await getDoc(doc(db, "jobs", j.id, "financials", "v1"));
              fin = finSnap.exists() ? finSnap.data() : {};
            } catch (e) {
              fin = {};
            }
            return { ...j, __fin: fin, __jobMoment: parseDate(fin.jobDate || j.startDate) };
          })
        );

        const byId = {};
        const byWo = {};
        totals.forEach((t) => {
          byId[t.id] = t;
          if (t.wo) byWo[String(t.wo)] = t;
        });

        const txIds = new Set(txRows.map((t) => t.id));
        const mirroredSubPayIds = new Set(
          txRows.filter((tx) => tx.sourceCollection === "subcontractorPay" && tx.sourceId).map((tx) => String(tx.sourceId))
        );
        const legacySubPayRows = subPayRows
          .filter((d) => !mirroredSubPayIds.has(String(d.id)) && !(d.txId && txIds.has(d.txId)))
          .map((data) => {
            const date =
              data.date ||
              (data.datePaid?.toDate ? moment(data.datePaid.toDate()).format("YYYY-MM-DD") : String(data.datePaid || data.createdAt || "").slice(0, 10));
            return {
              id: `subcontractorPay-${data.id}`,
              date,
              type: "expense",
              amount: num(data.amount ?? data.amountPaid),
              category: "Subcontractor",
              vendor: data.vendor || data.subcontractorName || data.userName || data.property || "Subcontractor",
              memo: data.note || "Subcontractor payment",
              method: data.method || "manual",
              jobId: data.jobId || null,
              sourceCollection: "subcontractorPay",
              sourceId: data.id,
            };
          });

        const withMoment = (list) => list.map((x) => ({ ...x, __dateMoment: parseDate(x.date) }));

        if (cancelled) return;
        setRows(withFin);
        setJobTotals({ byId, byWo });
        setUsers(usersData);
        setLaborDays(days);
        setSpendingDays(spend);
        setInvoices(invoiceRows);
        setTransactions(
          withMoment([...txRows, ...legacySubPayRows]).sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
        );
        setBankStatementEntries(
          withMoment(bankRows).sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Persist filters
  useEffect(() => {
    writeStorage("financialMode", mode);
    const m = parseDate(anchor) || moment();
    const stored = mode === "month" ? m.format("YYYY-MM") : mode === "year" ? String(m.year()) : m.format("YYYY-MM-DD");
    writeStorage("financialAnchor", stored);
  }, [mode, anchor]);

  // Normalize anchor whenever mode changes
  useEffect(() => {
    const m = parseDate(anchor) || moment();
    if (mode === "month") setAnchor(m.format("YYYY-MM"));
    else if (mode === "year") setAnchor(String(m.year()));
    else setAnchor(m.format("YYYY-MM-DD"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const { start, end } = useMemo(() => rangeFor(mode, anchor), [mode, anchor]);
  const startKey = start.format("YYYY-MM-DD");
  const endKey = end.format("YYYY-MM-DD");

  const usersById = useMemo(() => usersToMap(users), [users]);

  // Tech Daily Pay - the one labor source
  const labor = useMemo(
    () => summarizeDailyLabor(laborDays, usersById, startKey, endKey),
    [laborDays, usersById, startKey, endKey]
  );
  const laborByJob = useMemo(() => {
    const out = {};
    Object.entries(labor.byJob).forEach(([jobId, amount]) => {
      if (jobId === UNASSIGNED_JOB) return;
      const id = baseJobId(jobId);
      out[id] = (out[id] || 0) + amount;
    });
    return out;
  }, [labor]);

  // Spending (Daily Spending page)
  const filteredSpending = useMemo(
    () =>
      spendingDays.reduce(
        (acc, d) => {
          const key = String(d.date || d.id || "").slice(0, 10);
          if (!key || key < startKey || key > endKey) return acc;
          const cc = (d.creditCards || []).reduce((s, c) => s + num(c.amount), 0);
          const bk = (d.bankAccounts || []).reduce((s, b) => s + num(b.amount), 0);
          return { card: acc.card + cc, bank: acc.bank + bk };
        },
        { card: 0, bank: 0 }
      ),
    [spendingDays, startKey, endKey]
  );

  const filteredTx = useMemo(
    () => transactions.filter((t) => t.__dateMoment && t.__dateMoment.isBetween(start, end, null, "[]")),
    [transactions, start, end]
  );

  const cashTotals = useMemo(() => {
    const counted = filteredTx.filter((t) => !DO_NOT_COUNT_ACTIONS.has(t.accountingAction));
    const income = counted.filter((t) => t.type === "income").reduce((s, t) => s + num(t.amount), 0);
    const expense = counted.filter((t) => t.type === "expense").reduce((s, t) => s + num(t.amount), 0);
    return { income, expense, net: income - expense };
  }, [filteredTx]);

  const filteredBankEntries = useMemo(
    () => bankStatementEntries.filter((e) => e.__dateMoment && e.__dateMoment.isBetween(start, end, null, "[]")),
    [bankStatementEntries, start, end]
  );

  const bankStatementSummary = useMemo(
    () =>
      filteredBankEntries.reduce(
        (acc, entry) => {
          const amount = num(entry.amount);
          const counts = bankEntryCountsInBooks(entry);
          if (entry.type === "moneyIn") acc.rawMoneyIn += amount;
          if (entry.type === "moneyOut") acc.rawMoneyOut += amount;
          if (counts && entry.type === "moneyIn") acc.countedIncome += amount;
          if (counts && entry.type === "moneyOut") acc.countedExpense += amount;
          if (!counts) {
            acc.recordOnly += amount;
            acc.recordOnlyCount += 1;
          }
          acc.totalRows += 1;
          return acc;
        },
        { totalRows: 0, rawMoneyIn: 0, rawMoneyOut: 0, countedIncome: 0, countedExpense: 0, recordOnly: 0, recordOnlyCount: 0 }
      ),
    [filteredBankEntries]
  );

  const cashBySource = useMemo(
    () =>
      filteredTx.reduce((map, tx) => {
        const key = sourceLabel(tx);
        if (!map[key]) map[key] = { source: key, income: 0, expense: 0, count: 0 };
        if (DO_NOT_COUNT_ACTIONS.has(tx.accountingAction)) return map;
        if (tx.type === "income") map[key].income += num(tx.amount);
        if (tx.type === "expense") map[key].expense += num(tx.amount);
        map[key].count += 1;
        return map;
      }, {}),
    [filteredTx]
  );

  // Invoices by job, for recurring price in range
  const invoicesByJob = useMemo(() => {
    const byWo = {};
    rows.forEach((j) => {
      if (j.wo) byWo[String(j.wo)] = j.id;
    });
    const out = {};
    invoices.forEach((inv) => {
      if (["void", "canceled", "cancelled", "deleted"].includes(String(inv.status || "").toLowerCase())) return;
      const jobId = inv.jobId || inv.originalJobId || byWo[String(inv.wo || inv.invoiceNumber || "")];
      if (!jobId) return;
      out[jobId] = out[jobId] || [];
      out[jobId].push(inv);
    });
    return out;
  }, [invoices, rows]);

  const totalFor = (job) => jobTotals.byId[job.id] || jobTotals.byWo[String(job.wo)] || null;

  // Jobs in range + profitability
  const computed = useMemo(() => {
    let price = 0, materials = 0, laborSum = 0, misc = 0, overhead = 0, profit = 0;
    let actualLaborShown = 0;
    const items = [];

    rows.forEach((r) => {
      const f = r.__fin || {};
      const recurring = isRecurring(r);
      const finDateInRange = r.__jobMoment && r.__jobMoment.isSameOrAfter(start) && r.__jobMoment.isSameOrBefore(end);

      let p = 0;
      let priceNote = "";
      let visits = 0;

      if (recurring) {
        const isExcluded = makeOccurrenceDateMatcher(r, r.excludedDates || []);
        const isCompleted = makeOccurrenceDateMatcher(r, r.completedDates || []);
        const dates = getOccurrencesForJob(r, start.toDate(), end.toDate()).filter((k) => !isExcluded(k));
        const rangeInvoices = (invoicesByJob[r.id] || []).filter((inv) => {
          const key = String(inv.occurrenceKey || inv.dateOfService || inv.serviceDate || inv.invoiceDate || "").slice(0, 10);
          return key && key >= startKey && key <= endKey;
        });
        if (!dates.length && !rangeInvoices.length && !finDateInRange && !laborByJob[r.id]) return;
        visits = dates.length;
        // Invoiced visits use the invoice; done-but-not-invoiced visits use the per-visit fee.
        const isInvoiced = makeOccurrenceDateMatcher(r, [
          ...(r.invoicedDates || []),
          ...Object.keys(r.approvedInvoiceIdByDate || {}),
          ...(invoicesByJob[r.id] || []).map((inv) => String(inv.occurrenceKey || "").slice(0, 10)).filter(Boolean),
        ]);
        const doneNotInvoiced = dates.filter((k) => isCompleted(k) && !isInvoiced(k));
        p =
          rangeInvoices.reduce((s, inv) => s + invoiceTotal(inv), 0) +
          doneNotInvoiced.reduce((s, k) => s + recurringVisitFee(r, k), 0);
        priceNote = [
          rangeInvoices.length ? `${rangeInvoices.length} invoice(s)` : "",
          doneNotInvoiced.length ? `${doneNotInvoiced.length} done, not invoiced` : "",
        ].filter(Boolean).join(" + ") || "no visits done yet";
      } else {
        // Shown when its job date is in range, OR when techs logged daily pay
        // on it in range (labor only - price/costs stay in the job-date period).
        if (!finDateInRange && !(num(laborByJob[r.id]) > 0)) return;
        if (finDateInRange) {
          const jt = totalFor(r);
          p = jt && jt.total !== undefined && jt.total !== null && jt.total !== ""
            ? num(jt.total)
            : num(f.price) || firstMoney(r.estimatedTotal, r.contractAmount);
        } else {
          priceNote = "job date outside this period - labor only";
        }
      }

      // Job Financials costs (and one-time price) count once, in the period of the job date.
      const useFinCosts = finDateInRange;
      const mc = useFinCosts ? num(f.materialsCost) : 0;
      const mi = useFinCosts ? num(f.miscCost) : 0;
      const oh = useFinCosts ? finOverhead(f, p) : 0;
      const actualLabor = num(laborByJob[r.id]);
      const estLabor = useFinCosts ? num(f.laborCost) : 0;
      const lc = actualLabor > 0 ? actualLabor : estLabor;

      const gp = p - (mc + lc + mi + oh);
      const margin = p > 0 ? (gp / p) * 100 : 0;
      price += p;
      materials += mc;
      laborSum += lc;
      misc += mi;
      overhead += oh;
      profit += gp;

      if (actualLabor > 0) actualLaborShown += actualLabor;
      items.push({
        ...r,
        __recurring: recurring,
        __priceNote: priceNote,
        __priceInRange: recurring || finDateInRange,
        __visits: visits,
        __laborActual: actualLabor > 0,
        p, mc, lc, mi, oh, gp, margin,
      });
    });

    // Daily pay on jobs that are not in the table (deleted / missing jobs).
    const shownIds = new Set(items.map((i) => i.id));
    const missingJobLabor = Object.entries(laborByJob)
      .filter(([id]) => !shownIds.has(id))
      .reduce((s, [, amount]) => s + num(amount), 0);

    items.sort((a, b) => String(a.__fin?.jobDate || a.startDate || "").localeCompare(String(b.__fin?.jobDate || b.startDate || "")));
    const marginAll = price > 0 ? (profit / price) * 100 : 0;
    return {
      items,
      totals: { price, materials, labor: laborSum, misc, overhead, profit, marginAll },
      laborCheck: { actualLaborShown, missingJobLabor },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, jobTotals, start, end, startKey, endKey, invoicesByJob, laborByJob]);

  // Quick net: job price minus Tech Daily Pay and Daily Spending (a rough
  // operating view; the ledger-based Net Cash above is the bank view).
  const quickNet = useMemo(
    () => computed.totals.price - labor.total - filteredSpending.card - filteredSpending.bank,
    [computed, labor, filteredSpending]
  );

  const handleAddTransaction = async () => {
    const amt = Number(txForm.amount);
    if (!txForm.date || isNaN(amt) || amt <= 0) {
      alert("Enter a date and an amount greater than 0.");
      return;
    }
    const payload = {
      date: txForm.date,
      type: txForm.type,
      amount: amt,
      category: txForm.category,
      vendor: txForm.vendor || "",
      memo: txForm.memo || "",
      jobId: txForm.jobId || null,
      method: txForm.method || "manual",
      createdAt: serverTimestamp(),
    };
    try {
      const ref = await addDoc(collection(db, "transactions"), payload);
      setTransactions((prev) => [{ id: ref.id, ...payload, __dateMoment: parseDate(payload.date) }, ...prev]);
      setTxForm((f) => ({ ...f, amount: "", vendor: "", memo: "" }));
    } catch (err) {
      console.error("Could not add transaction", err);
      alert("Could not save the transaction.");
    }
  };

  const savePrice = async (job) => {
    const draft = priceDrafts[job.id];
    if (draft === undefined) return;
    const value = Number(draft);
    if (draft === "" || !Number.isFinite(value)) return;
    const existing = totalFor(job);
    const docId = existing?.id || job.id;
    try {
      await setDoc(
        doc(db, "job_totals", String(docId)),
        { jobId: job.id, wo: String(job.wo || existing?.wo || job.id), total: value, source: "manual", updatedAt: serverTimestamp() },
        { merge: true }
      );
      const saved = { ...(existing || {}), id: docId, jobId: job.id, wo: String(job.wo || existing?.wo || job.id), total: value };
      setJobTotals((prev) => ({
        byId: { ...prev.byId, [docId]: saved },
        byWo: job.wo ? { ...prev.byWo, [String(job.wo)]: saved } : prev.byWo,
      }));
      setPriceDrafts((prev) => {
        const next = { ...prev };
        delete next[job.id];
        return next;
      });
    } catch (err) {
      console.error("Could not save price", err);
      alert("Could not save the price.");
    }
  };

  // Adaptive date input
  const dateInputType = mode === "month" ? "month" : mode === "year" ? "number" : "date";
  const anchorMoment = parseDate(anchor) || moment();
  const dateInputValue =
    mode === "month" ? anchorMoment.format("YYYY-MM") : mode === "year" ? String(anchorMoment.year()) : anchorMoment.format("YYYY-MM-DD");

  const tabBtn = (key, label) => (
    <button className="smallazul-button" onClick={() => setTab(key)} style={{ opacity: tab === key ? 1 : 0.6 }}>
      {label}
    </button>
  );

  return (
    <div style={{ padding: 16, maxWidth: "100%", boxSizing: "border-box" }}>
      <h2 style={{ margin: "0 0 4px" }}>Financials Overview</h2>
      <div style={{ color: "#64748b", fontSize: 14 }}>Priority Pest Defense LLC</div>

      <div style={S.note}>
        <strong>Cash In / Cash Out</strong> come from the transactions ledger (Deposits, Expenses, Bank Statement and Job
        Estimate all post there). Bank lines marked "do not count" (transfers, owner money, matched payments,
        corrections) are left out. <strong>Labor</strong> comes from the Tech Daily Pay page. On each job, labor is the
        actual Tech Daily Pay for that job when it was recorded, otherwise the estimate from Job Financials - never both.
      </div>

      {loading && <div style={{ marginTop: 6, fontSize: 12, color: "#64748b" }}>Loading…</div>}

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <select value={mode} onChange={(e) => setMode(e.target.value)}>
          <option value="day">Day</option>
          <option value="week">Week</option>
          <option value="month">Month</option>
          <option value="year">Year</option>
        </select>
        <input
          type={dateInputType}
          value={dateInputValue}
          min={mode === "year" ? "2000" : undefined}
          max={mode === "year" ? "2100" : undefined}
          onChange={(e) => setAnchor(e.target.value)}
        />
        <span style={{ fontSize: 13, color: "#475569" }}>
          {start.format("MMM D, YYYY")} – {end.format("MMM D, YYYY")}
        </span>
        <button className="smallazul-button" onClick={() => navigate("/OverheadSettings")}>Overhead Settings</button>
        <button className="smallazul-button" onClick={() => (onOpenTab ? onOpenTab("dailypay") : navigate("/DailyLabor"))}>Tech Daily Pay</button>
        <button className="smallazul-button" onClick={() => navigate("/DailySpending")}>Daily Spending</button>
      </div>

      <div style={S.grid(170)}>
        <Card label="Cash In" value={formatMoney(cashTotals.income)} sub="Ledger income" />
        <Card label="Cash Out" value={formatMoney(cashTotals.expense)} sub="Ledger expenses (incl. payroll paid)" />
        <Card label="Net Cash" value={formatMoney(cashTotals.net)} color={cashTotals.net >= 0 ? "green" : "red"} />
        <Card label="Tech Daily Pay" value={formatMoney(labor.total)} sub={`Labor earned · ${Object.keys(labor.byUser).length} tech(s)`} />
        <Card label="Bank Statement Rows" value={bankStatementSummary.totalRows} />
        <Card label="Record Only Rows" value={`${bankStatementSummary.recordOnlyCount} / ${formatMoney(bankStatementSummary.recordOnly)}`} />
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        {tabBtn("jobs", "Jobs (Profitability)")}
        {tabBtn("transactions", "Transactions (Cash In/Out)")}
        {tabBtn("bankStatements", "Bank Statements / Audit")}
      </div>

      {/* ===== JOBS TAB ===== */}
      {tab === "jobs" && (
        <>
          <div style={S.grid(150)}>
            <Card label="Total Price" value={formatMoney(computed.totals.price)} />
            <Card label="Materials" value={formatMoney(computed.totals.materials)} />
            <Card label="Labor" value={formatMoney(computed.totals.labor)} sub="Actual daily pay, else estimate" />
            <Card label="Misc" value={formatMoney(computed.totals.misc)} />
            <Card label="Overhead" value={formatMoney(computed.totals.overhead)} />
            <Card
              label="Gross Profit"
              value={`${formatMoney(computed.totals.profit)} (${computed.totals.marginAll.toFixed(1)}%)`}
              color={computed.totals.profit >= 0 ? "green" : "red"}
            />
          </div>

          <div style={S.grid(170)}>
            <Card label="Tech Daily Pay (all)" value={formatMoney(labor.total)} sub="From the Tech Daily Pay page" />
            <Card label="Daily Pay Not Tied to a Job" value={formatMoney(labor.unassigned)} sub="Pick jobs on Tech Daily Pay" />
            <Card label="Daily Spending - Card" value={formatMoney(filteredSpending.card)} />
            <Card label="Daily Spending - Bank" value={formatMoney(filteredSpending.bank)} />
            <Card
              label="Quick Net"
              value={formatMoney(quickNet)}
              color={quickNet >= 0 ? "green" : "red"}
              sub="Job price − Tech Daily Pay − Daily Spending"
            />
          </div>

          <div className="table-container" style={{ overflowX: "auto", marginTop: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>WO#</th><th>Site</th><th>Service</th>
                  <th>Price</th><th>Materials</th><th>Labor</th><th>Misc</th><th>Overhead</th>
                  <th>Profit</th><th>Margin</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {computed.items.map((r) => {
                  const jt = totalFor(r);
                  const priceValue =
                    priceDrafts[r.id] !== undefined ? priceDrafts[r.id] : jt?.total ?? (num(r.__fin?.price) || "");
                  return (
                    <tr key={r.id}>
                      <td>
                        {r.__fin?.jobDate || r.startDate || ""}
                        {r.__recurring && <span style={S.tag("#ede9fe", "#5b21b6")}>Recurring · {r.__visits} visit(s)</span>}
                      </td>
                      <td>{r.wo}</td>
                      <td>{r.siteName || r.propertyName}</td>
                      <td>{r.serviceType}</td>
                      <td>
                        {r.__recurring || !r.__priceInRange ? (
                          <span title={r.__recurring ? "Recurring jobs use the invoices for visits in this period" : "Price counts in the period of the job date"}>
                            {formatMoney(r.p)}
                            <div style={{ fontSize: 11, color: "#64748b" }}>{r.__priceNote}</div>
                          </span>
                        ) : (
                          <input
                            type="number"
                            value={priceValue}
                            onChange={(e) => setPriceDrafts((prev) => ({ ...prev, [r.id]: e.target.value }))}
                            onBlur={() => savePrice(r)}
                            style={{ width: 90 }}
                          />
                        )}
                      </td>
                      <td>{formatMoney(r.mc)}</td>
                      <td>
                        {formatMoney(r.lc)}
                        {r.lc > 0 && (
                          <span style={r.__laborActual ? S.tag("#dcfce7", "#166534") : S.tag("#fef3c7", "#92400e")}>
                            {r.__laborActual ? "actual" : "estimate"}
                          </span>
                        )}
                      </td>
                      <td>{formatMoney(r.mi)}</td>
                      <td>{formatMoney(r.oh)}</td>
                      <td style={{ color: r.gp >= 0 ? "green" : "red" }}>{formatMoney(r.gp)}</td>
                      <td>{r.margin.toFixed(1)}%</td>
                      <td>
                        <button className="yellow-button" onClick={() => navigate(`/JobFinancials/${r.id}`)}>Edit</button>
                      </td>
                    </tr>
                  );
                })}
                {computed.items.length === 0 && (
                  <tr><td colSpan={12} style={{ textAlign: "center" }}>No jobs in range.</td></tr>
                )}
                {computed.laborCheck.missingJobLabor > 0.004 && (
                  <tr style={{ background: "#fff7ed" }}>
                    <td colSpan={6}>Tech Daily Pay on jobs that are deleted or missing</td>
                    <td>{formatMoney(computed.laborCheck.missingJobLabor)}</td>
                    <td colSpan={5} style={{ fontSize: 12, color: "#64748b" }}>Not in the rows above. Re-pick the jobs for those days on Tech Daily Pay.</td>
                  </tr>
                )}
                {labor.unassigned > 0.004 && (
                  <tr style={{ background: "#fff7ed" }}>
                    <td colSpan={6}>Tech Daily Pay not tied to any job</td>
                    <td>{formatMoney(labor.unassigned)}</td>
                    <td colSpan={5} style={{ fontSize: 12, color: "#64748b" }}>Pick the jobs each tech worked on Tech Daily Pay.</td>
                  </tr>
                )}
                <tr>
                  <td colSpan={6} style={{ fontWeight: 700 }}>Tech Daily Pay check: actual on jobs above + missing jobs + not tied to a job</td>
                  <td style={{ fontWeight: 700 }}>
                    {formatMoney(computed.laborCheck.actualLaborShown + computed.laborCheck.missingJobLabor + labor.unassigned)}
                  </td>
                  <td colSpan={5} style={{ fontSize: 12, color: "#64748b" }}>
                    = Tech Daily Pay total {formatMoney(labor.total)}. (Rows marked "estimate" are not daily pay.)
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ===== TRANSACTIONS TAB ===== */}
      {tab === "transactions" && (
        <div style={{ marginTop: 16 }}>
          <div style={{ ...S.card, marginBottom: 12 }}>
            <strong>Add Transaction</strong>
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
              For quick manual entries only. Deposits, pay and bills should be entered on their own pages (they post
              here automatically) so they are not counted twice.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 8, marginTop: 10 }}>
              <input type="date" value={txForm.date} onChange={(e) => setTxForm((f) => ({ ...f, date: e.target.value }))} />
              <select value={txForm.type} onChange={(e) => setTxForm((f) => ({ ...f, type: e.target.value }))}>
                <option value="expense">Expense</option>
                <option value="income">Income</option>
              </select>
              <input
                type="number"
                placeholder="Amount"
                value={txForm.amount}
                onChange={(e) => setTxForm((f) => ({ ...f, amount: e.target.value }))}
              />
              <select value={txForm.category} onChange={(e) => setTxForm((f) => ({ ...f, category: e.target.value }))}>
                {DASHBOARD_CATEGORIES.map((category) => (
                  <option key={category} value={category}>{category}</option>
                ))}
              </select>
              <input placeholder="Vendor" value={txForm.vendor} onChange={(e) => setTxForm((f) => ({ ...f, vendor: e.target.value }))} />
              <select value={txForm.method} onChange={(e) => setTxForm((f) => ({ ...f, method: e.target.value }))}>
                <option value="manual">Manual</option>
                <option value="card">Card</option>
                <option value="bank">Bank</option>
                <option value="cash">Cash</option>
              </select>
              <input
                style={{ gridColumn: "1 / -1" }}
                placeholder="Memo / details"
                value={txForm.memo}
                onChange={(e) => setTxForm((f) => ({ ...f, memo: e.target.value }))}
              />
              <select
                style={{ gridColumn: "1 / -1" }}
                value={txForm.jobId}
                onChange={(e) => setTxForm((f) => ({ ...f, jobId: e.target.value }))}
              >
                <option value="">(No Job Linked)</option>
                {rows.map((j) => (
                  <option key={j.id} value={j.id}>
                    {j.wo ? `WO ${j.wo} - ` : ""}{j.siteName || j.id}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ marginTop: 10 }}>
              <button className="yellow-button" onClick={handleAddTransaction}>Add</button>
            </div>
          </div>

          <div style={S.grid(170)}>
            <Card label="Cash In" value={formatMoney(cashTotals.income)} />
            <Card label="Cash Out" value={formatMoney(cashTotals.expense)} />
            <Card label="Net Cash" value={formatMoney(cashTotals.net)} color={cashTotals.net >= 0 ? "green" : "red"} />
          </div>

          <div className="table-container" style={{ overflowX: "auto", marginTop: 16 }}>
            <h3>Cash by Source</h3>
            <table>
              <thead>
                <tr><th>Source</th><th>Rows</th><th>Income</th><th>Expense</th><th>Net</th></tr>
              </thead>
              <tbody>
                {Object.values(cashBySource).map((row) => (
                  <tr key={row.source}>
                    <td>{row.source}</td>
                    <td>{row.count}</td>
                    <td>{formatMoney(row.income)}</td>
                    <td>{formatMoney(row.expense)}</td>
                    <td style={{ color: row.income - row.expense >= 0 ? "green" : "red" }}>{formatMoney(row.income - row.expense)}</td>
                  </tr>
                ))}
                {Object.values(cashBySource).length === 0 && (
                  <tr><td colSpan={5} style={{ textAlign: "center" }}>No transaction sources in range.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="table-container" style={{ overflowX: "auto", marginTop: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Type</th><th>Category</th><th>Vendor</th><th>Source</th><th>Memo</th><th>Job</th><th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredTx.map((t) => {
                  const job = t.jobId ? rows.find((r) => r.id === baseJobId(t.jobId)) : null;
                  const notCounted = DO_NOT_COUNT_ACTIONS.has(t.accountingAction);
                  return (
                    <tr key={t.id} style={notCounted ? { background: "#fff7ed" } : undefined}>
                      <td>{t.date}</td>
                      <td>{t.type}{notCounted ? " (not counted)" : ""}</td>
                      <td>{t.category}</td>
                      <td>{t.vendor || t.payer}</td>
                      <td>{sourceLabel(t)}</td>
                      <td>{t.memo}</td>
                      <td>{job ? `${job.wo ? `WO ${job.wo} - ` : ""}${job.siteName || job.id}` : ""}</td>
                      <td style={{ color: t.type === "expense" ? "red" : "green" }}>
                        {t.type === "expense" ? "-" : "+"}{formatMoney(num(t.amount))}
                      </td>
                    </tr>
                  );
                })}
                {filteredTx.length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: "center" }}>No transactions in range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ===== BANK STATEMENTS TAB ===== */}
      {tab === "bankStatements" && (
        <div style={{ marginTop: 16 }}>
          <div style={S.grid(170)}>
            <Card label="Rows in Range" value={bankStatementSummary.totalRows} />
            <Card label="Raw Money In" value={formatMoney(bankStatementSummary.rawMoneyIn)} />
            <Card label="Raw Money Out" value={formatMoney(bankStatementSummary.rawMoneyOut)} />
            <Card label="Counted Income" value={formatMoney(bankStatementSummary.countedIncome)} />
            <Card label="Counted Expense" value={formatMoney(bankStatementSummary.countedExpense)} />
            <Card label="Record Only" value={`${bankStatementSummary.recordOnlyCount} / ${formatMoney(bankStatementSummary.recordOnly)}`} />
          </div>

          <div className="table-container" style={{ overflowX: "auto", marginTop: 16 }}>
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Bank / Last 4</th><th>Type</th><th>Category</th><th>Accounting</th>
                  <th>Vendor / Payer</th><th>WO / Invoice</th><th>Description</th><th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {filteredBankEntries.map((entry) => {
                  const action = getDefaultAccountingAction(entry);
                  const counts = bankEntryCountsInBooks(entry);
                  return (
                    <tr key={entry.id} style={counts ? undefined : { background: "#fff7ed" }}>
                      <td>{entry.date || ""}</td>
                      <td>{[entry.bankName, entry.accountLast4 ? `•••• ${entry.accountLast4}` : ""].filter(Boolean).join(" ")}</td>
                      <td>{entry.type === "moneyIn" ? "Money In" : "Money Out"}</td>
                      <td>{entry.effectiveCategory || entry.category || ""}</td>
                      <td>{ACCOUNTING_ACTION_LABELS[action] || action}</td>
                      <td>{entry.vendorOrPayer || ""}</td>
                      <td>{[entry.wo, entry.invoiceNumber].filter(Boolean).join(" / ")}</td>
                      <td>{entry.description || entry.transactionDetail || ""}</td>
                      <td style={{ color: entry.type === "moneyOut" ? "red" : "green" }}>
                        {entry.type === "moneyOut" ? "-" : "+"}{formatMoney(entry.amount)}
                      </td>
                    </tr>
                  );
                })}
                {filteredBankEntries.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: "center" }}>No bank statement rows in range.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
